import { memo, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, Layout, notification, Card, Empty, Typography } from "antd";
import { useMsal } from "@azure/msal-react";
import { useLogin } from "hooks/useLogin";
import "./style.scss";
import { IDeleteRequestState } from "./types";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import { getLoggedInUser } from "utils/utils";
import { confirmdeletion, getdeletionrequest, rejectdeletion } from "./boardApi";
import { CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { axiosIntercept } from "http/axiosIntercept";

/**
 * Initial empty state for a delete request.
 * @type {IDeleteRequest}
 */
const initialDeleteRequest: IDeleteRequestState = {
  space_name: "",
  "request-id": "",
  domain_name: "",
  otherOwners: [],
  deletionRequestedTime: "",
  bucket_name: "",
  reason_for_deletion: "",
  status: "",
  region_name: "",
  selected_prefixes: [],
  loggedInUserEmail: "",
  is_delete_marker: undefined,
  file_key: undefined,
  version_id: undefined,
};

/**
 * Board page component for handling and displaying deletion requests.
 *
 * - Fetches deletion request data based on the route param.
 * - Handles approve/reject actions for deletion requests.
 * - Shows confirmation modals and error messages.
 *
 * @component
 */
const Board = () => {
  const [deleteRequest, setDeleteRequest] = useState<IDeleteRequestState>(initialDeleteRequest);
  const navigate = useNavigate();
  const msalInstance = useMsal();
  const accountName: string = msalInstance.accounts[0]?.username
    ? msalInstance.accounts[0].username
    : "";
  const [isSaveClicked, setSaveClicked] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { showAuthorizationErrorModal } = useLogin(msalInstance);
  const location = useLocation();

  const useQuery = () => {
    return new URLSearchParams(location.search);
  };
  const query = useQuery();
  const requestid = query.get("id") || ""; // Get the request ID from the query parameter
  const { Header, Content, Footer } = Layout;
	axiosIntercept(msalInstance);
  /**
   * Fetches the deletion request data and updates state.
   * Uses a mock object for demonstration; replace with API data as needed.
   * @async
   * @returns {Promise<void>}
   */
  const fetchData = async () => {

    const response = await getdeletionrequest(msalInstance, requestid);
    if (typeof response?.data !== "string" && response?.data) {
      const deleteRequestData: IDeleteRequestState = {
        space_name: response?.data?.space_name || "",
        "request-id": response?.data?.["request-id"] || "",
        domain_name: response?.data?.domain_name || "",
        otherOwners: JSON.parse(response?.data?.otherOwners ?? "[]"),
        deletionRequestedTime: response?.data?.deletionRequestedTime || "",
        bucket_name: response?.data?.bucket_name || "",
        reason_for_deletion: response?.data?.reason_for_deletion || "",
        status: response?.data?.status || "",
        region_name: response?.data?.region_name || "",
        selected_prefixes: JSON.parse(response?.data?.selected_prefixes || "[]"),
        loggedInUserEmail: response?.data?.loggedInUserEmail || "",
        is_delete_marker: response?.data?.is_delete_marker,
        file_key: response?.data?.file_key,
        version_id: response?.data?.version_id,
      };
      setDeleteRequest(deleteRequestData);
    }
    if (response?.error) {
      notification.error({
        message: response.error || "Something went wrong",
        duration: 5,
      });
    }
  };

  useEffect(() => {
    requestid && fetchData();
  }, [requestid]);

  /**
   * Handles deletion confirmation or rejection.
   * Shows appropriate modal based on the result.
   * @async
   * @param {boolean} confirm - Whether the deletion is confirmed (true) or rejected (false).
   * @returns {Promise<void>}
   */
  const deletion = async (confirm: boolean) => {
    setSaveClicked(true);
    try {
      let result;
      if (confirm) {
        const result1: {
          data: boolean | null;
          error: null | string;
        } = await confirmdeletion(msalInstance, requestid);
        result = result1;
      } else {
        const result2: {
          data: boolean | null;
          error: null | string;
        } = await rejectdeletion(msalInstance, requestid);
        result = result2;
      }
      if (result.error === "token expired") {
        return showAuthorizationErrorModal();
      }

      if (result?.data) {
        if (confirm) {
          return Modal.error({
            title: "Deletion Confirmed",
            icon: <CheckCircleOutlined style={{ color: "green" }} />, 
            content: "The removal of the files have been initiated. You will get a confirmation email when done.",
            okText: "Ok",
          });
        } else {
          return Modal.error({
            title: "Deletion Rejected",
            icon: <InfoCircleOutlined />,
            content: "You have rejected the request to delete files. And email is sent to the space owners informing about the rejection.",
            okText: "Ok",
          });
        }
      }

      if (result.error) {
        return Modal.error({
          title: "Error",
          content: result.error,
          okText: "Ok",
        });
      }
    } finally {
      setSaveClicked(false);
      setIsOpenModal(false);
      fetchData();
    }
  };

  const loggedInUserEmail = getLoggedInUser();
  const haveAccess = deleteRequest?.otherOwners?.some((email) => email === loggedInUserEmail) ?? false;

  return (
    <Layout>
      <Header className="header-no-padding" />
      <Content className="content-margin">
        {
          (!deleteRequest.space_name || !requestid) ?
            <Card className="centered-card">
              <Empty
                description={
                  <Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                      {"No delete request to display."}
                    </Typography.Text>
                  </Typography.Text>
                }
              >
              </Empty>
            </Card>
            : <DeleteConfirmation
              request={deleteRequest}
              onApprove={() => deletion(true)}
              onReject={() => deletion(false)}
              haveAccess={haveAccess}
            />
        }

      </Content>
    </Layout>
  );
};

export default memo(Board);
